import * as React from 'react';

import {EmptyAddCard} from 'shells/card/empty-add-card';
import {StackLegacy} from 'shells/layout/stack/stack-legacy';
import {LoadingPage} from 'shells/loading-page/loading-page';
import {SavedSettingToast} from 'shells/saved-setting-toast/saved-setting-toast';
import {useManageSaveState} from 'shells/saved-setting-toast/use-manage-save-state';
import {SectionLabel} from 'shells/typography/section-label';

import type {
    SmsDripSequenceFragment as DripSequence,
    SmsDripEditionFragment as DripEdition,
    SmsDripSequenceUpdateInput as DripSequenceUpdateInput,
    SmsEditionUpdateInput as EditionUpdateInput,
    StartTriggerSetInput,
    DripSequenceGoalSetInput,
} from 'nutshell-graphql-types';

import {DripSequenceEditionsListItem} from '../../../drip-sequences/drip-sequence-page/drip-sequence-editions-list-item';
import {GoalCardWrapper} from '../../../drip-sequences/drip-sequence-page/goal-card-wrapper';
import {NextStepCardWrapper} from '../../../drip-sequences/drip-sequence-page/next-step-card-wrapper';
import {StartTriggerCardWrapper} from '../../../drip-sequences/drip-sequence-page/start-trigger-card-wrapper';
import {smsGoalTypeValues, smsTriggerTypeValues} from '../../../drip-sequences/sidebar/constants';
import {convertSmsEngagementStatsToGeneralStatObject} from '../../../utils/helpers';
import {useCheckAndResetClientConfigsOnMount} from '../../../utils/use-check-and-reset-client-configs-on-mount';

import {DripSequencesDetailsForm} from '../drip-sequences-details/drip-sequences-details-form';

import styles from './drip-sequence-page.module.css';

type Props = {
    dripSequence: DripSequence;
    editions: DripEdition[];
    isLoading: boolean;
    isDraft: boolean;
    refetchDripSequence: () => Promise<any>;
    refetchDripEditions: () => Promise<any>;
    onCreateEdition: () => void;
    onEditionUpdate: (arg1: EditionUpdateInput) => Promise<any>;
    onDripSequenceUpdate: (input: DripSequenceUpdateInput) => Promise<any>;
    onRemoveEdition: (editionId: string, campaignId: string) => Promise<any>;
    onSetStartTrigger: (input: StartTriggerSetInput) => Promise<any>;
    onSetGoal: (input: DripSequenceGoalSetInput) => Promise<any>;
};

export function DripSequenceMessage(props: Props) {
    const {dripSequence, editions} = props;

    const [isSaving, setIsSaving] = React.useState<boolean>(false);
    const [isEditingStartTrigger, setIsEditingStartTrigger] = React.useState<
        string | null | undefined
    >(null);

    const {toastState: saveToastState} = useManageSaveState(isSaving, 1000);

    useCheckAndResetClientConfigsOnMount([
        {
            name: 'isEditingStartTrigger',
            action: () => {
                setIsEditingStartTrigger(NutClientConfig.isEditingStartTrigger);
            },
            timeout: 1000,
        },
    ]);

    return (
        <>
            <div className='flex full-height'>
                <div className={styles['left-container']}>
                    <StartTriggerCardWrapper
                        isLoading={props.isLoading}
                        dripSequence={dripSequence}
                        editions={editions}
                        onSetStartTrigger={props.onSetStartTrigger}
                        onEditionUpdate={(editionId, sendDelay) => {
                            return props.onEditionUpdate({editionId, sendDelay});
                        }}
                        isEditingStartTrigger={isEditingStartTrigger}
                        setIsEditingStartTrigger={(value) => setIsEditingStartTrigger(value)}
                        isTriggerEditable={Boolean(
                            dripSequence &&
                                dripSequence.status !== 'CANCELLED' &&
                                dripSequence.status !== 'RETIRED'
                        )}
                        initialDelay={
                            // @ts-expect-error
                            editions.length > 0 && editions[0].delay
                                ? {
                                      // @ts-expect-error
                                      unit: editions[0].delay.unit,
                                      // @ts-expect-error
                                      timespan: editions[0].delay.timespan,
                                  }
                                : null
                        }
                        variant={
                            dripSequence &&
                            dripSequence.status !== 'ACTIVE' &&
                            dripSequence.status !== 'GENERATING' &&
                            dripSequence.status !== 'GENERATED_WITH_ISSUES'
                                ? dripSequence.status
                                : undefined
                        }
                        triggerTypeValues={smsTriggerTypeValues}
                    />
                    <div className={styles['drip-edition-list-container']}>
                        {props.isLoading ? (
                            <div className={styles['loading-page-container']}>
                                <LoadingPage padding={0} />
                            </div>
                        ) : (
                            <>
                                {props.editions.length > 0 && (
                                    <StackLegacy spacing={16}>
                                        {props.editions.map((edition) => (
                                            <div key={edition.id} id={edition.id}>
                                                <DripSequenceEditionsListItem
                                                    edition={edition}
                                                    onRemoveEdition={(editionId: string) => {
                                                        props
                                                            .onRemoveEdition(
                                                                editionId,
                                                                props.dripSequence.id
                                                            )
                                                            .then(() => {
                                                                props.refetchDripEditions();
                                                            });
                                                    }}
                                                    onEditionUpdate={(editionId, sendDelay) => {
                                                        return props.onEditionUpdate({
                                                            editionId,
                                                            sendDelay,
                                                        });
                                                    }}
                                                    // @ts-expect-error
                                                    firstEditionId={props.editions[0].id}
                                                    refetchDripEditions={props.refetchDripEditions}
                                                    engagementStats={convertSmsEngagementStatsToGeneralStatObject(
                                                        edition.engagementStats
                                                    )}
                                                />
                                            </div>
                                        ))}
                                    </StackLegacy>
                                )}
                                {props.isDraft && (
                                    <div className={styles['add-message-container']}>
                                        <EmptyAddCard
                                            onClick={props.onCreateEdition}
                                            text='Add a message'
                                            background='off-white'
                                            size='small'
                                        />
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                </div>
                <div className={styles['right-container']}>
                    <GoalCardWrapper
                        isLoading={props.isLoading}
                        dripSequence={dripSequence}
                        // @ts-expect-error
                        editions={editions}
                        onSetGoal={props.onSetGoal}
                        onDripSequenceUpdate={(input) => {
                            return props.onDripSequenceUpdate({
                                id: input.id,
                                isStopOnGoal: input.isStopOnGoal,
                            });
                        }}
                        variant={
                            dripSequence &&
                            dripSequence.status !== 'ACTIVE' &&
                            dripSequence.status !== 'GENERATING' &&
                            dripSequence.status !== 'GENERATED_WITH_ISSUES'
                                ? dripSequence.status
                                : undefined
                        }
                        goalTypeValues={smsGoalTypeValues}
                    />
                    {dripSequence && dripSequence.goal ? (
                        <NextStepCardWrapper
                            isLoading={props.isLoading}
                            dripSequenceId={dripSequence.id}
                            goal={dripSequence.goal}
                            onSetGoal={props.onSetGoal}
                            variant={
                                props.dripSequence.status !== 'ACTIVE' &&
                                props.dripSequence.status !== 'GENERATING' &&
                                props.dripSequence.status !== 'GENERATED_WITH_ISSUES'
                                    ? props.dripSequence.status
                                    : undefined
                            }
                        />
                    ) : undefined}
                    <SectionLabel>
                        <SavedSettingToast state={saveToastState}>
                            Drip sequence details
                        </SavedSettingToast>
                    </SectionLabel>
                    <DripSequencesDetailsForm
                        dripSequence={props.dripSequence}
                        onDripSave={(saveData) => {
                            setIsSaving(true);

                            return props.onDripSequenceUpdate(saveData).then(() => {
                                props.refetchDripSequence().then(() => {
                                    setIsSaving(false);
                                });
                            });
                        }}
                    />
                </div>
            </div>
        </>
    );
}
